import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskedLine } from "../components/Reveal";

export const Hero = ({ content }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  const goQuote = () => document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
  const goProducts = () => document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20" data-testid="hero-section">
      <div className="absolute inset-0">
        <motion.img
          src={content.hero_image || "https://images.unsplash.com/photo-1602037299897-7e9f4061c1d5?crop=entropy&cs=srgb&fm=jpg&q=85"}
          alt="Custom printed cardboard tubes"
          style={{ y: imgY }}
          initial={{ scale: 1.12, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-[115%] object-cover opacity-30"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D10] via-[#0B0D10]/80 to-[#0B0D10]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] via-transparent to-[#0B0D10]/60" />
      </div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-mono-spec text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#D9A76A] mb-7"
          data-testid="hero-kicker"
        >
          {content.hero_kicker}
        </motion.p>
        <h1 className="font-serif-display font-medium tracking-tight leading-[1.05] text-[#F5F3EF]" data-testid="hero-heading">
          <MaskedLine delay={0.35} className="text-4xl sm:text-5xl lg:text-7xl">{content.hero_line1}</MaskedLine>
          <MaskedLine delay={0.5} className="text-4xl sm:text-5xl lg:text-7xl">
            <span className="foil-text italic">{content.hero_line2}</span>
          </MaskedLine>
          <MaskedLine delay={0.65} className="text-4xl sm:text-5xl lg:text-7xl">{content.hero_line3}</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#A1A8B3] font-light leading-relaxed"
          data-testid="hero-sub"
        >
          {content.hero_sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button onClick={goQuote} data-testid="hero-quote-btn" className="gold-btn pulse-ring text-[#0B0D10] font-semibold text-sm px-8 py-4 rounded-full flex items-center gap-2">
            Start your quote <ArrowRight size={16} />
          </button>
          <button onClick={goProducts} data-testid="hero-explore-btn" className="ghost-btn border border-white/15 text-[#F5F3EF] text-sm font-medium px-8 py-4 rounded-full">
            Explore the tubes
          </button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-12 flex flex-wrap gap-3"
          data-testid="hero-spec-pills"
        >
          {[`MOQ ${content.moq} units`, `Dispatch from ${content.lead_time_weeks} weeks`, "12 standard diameters", "Pantone · CMYK · Foil · Spot UV"].map((s) => (
            <span key={s} className="font-mono-spec text-[10px] uppercase tracking-[0.2em] text-[#D9A76A] border border-amber-500/20 rounded-full px-4 py-2 bg-[#14171D]/60">
              {s}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={goProducts}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#666F7D] hover:text-[#D4AF37] transition-colors"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={20} />
        </motion.div>
      </motion.button>
    </section>
  );
};
